import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Prompt, useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { Stack, Tooltip } from "@mui/material";
import Grid, { Grid2Props } from "@mui/material/Unstable_Grid2";
import { DatePicker, TimeField } from "@mui/x-date-pickers";
import TextField from "@jbhi-fi/lanyard-ui/components/TextField/TextField";
import { AutoCompleteType, InputType } from "@jbhi-fi/lanyard-ui/components/TextField/TextField.types";
import {
  Button,
  DatePickerProvider,
  ImageUploadField,
  SelectField,
} from "src/components/Form";
import {
  MessageTypes,
  MessageSubtypes,
  ConditionTypes,
  conditionTypeOptions,
  messageTypesOptions,
  messageSubtypesOptions,
  specialOfferSubtypesOptions,
  servicesSubtypesOptions,
  allSubtypesOptions,
} from "src/models/messaging";
import { getConsts } from "src/utils/consts";
import { CreateMessageModel } from "src/validationSchemas";
import { ProductSelect } from "./ProductSelect";
import moment from "moment";
import  MessageRichTextEditor  from "./MessageRichTextEditor";
import { getSubTypeLabel } from "src/models/messaging/MessageSubtypeLabels";
import InfoIconOutlined from '@mui/icons-material/InfoOutlined';
import GroupedSpecificationsDropdown from "./GroupedSpecificationsDropdown";
import { ProductGroupSelect } from "./ProductGroupSelect";
import { ProductCategorySelect } from "./ProductCategorySelect";

const useStyles = makeStyles((theme) => ({
  onlineTagInput: {
    flexGrow: 1,
    "& input[disabled]": { color: theme.palette.grey[400] },
  },
}));

const gridBorder = {
  border: 1,
  borderColor: "grey.400",
} as Grid2Props;

const gridSpacing = {
  paddingY: 4,
  paddingX: 2,
  marginTop: 3,
  marginX: 2,
  marginBottom: 0,
  spacing: 3,
} as Grid2Props;

type MessageFormProps = {
  onFormSubmit: (data: CreateMessageModel) => void;
  onFormError: (data: any) => void;
};

export const MessageForm: React.FC<MessageFormProps> = (props: MessageFormProps) => {
  const classes = useStyles();
  const history = useHistory();

  const [isOnlineTagDisabled, setIsOnlineTagDisabled] = useState(true);

  const formContext = useFormContext();
  const { dirtyFields } = formContext.formState;
  const typeWatch = formContext.watch("type");
  const subTypeWatch = formContext.watch("subType");
  const hasDefaultOnlineTagWatch = formContext.watch("hasDefaultOnlineTag");
  const conditionTypesWatch = formContext.watch("conditionTypes") as ConditionTypes[];
  const startTimeWatch = formContext.watch("startTime");
  const finishTimeWatch = formContext.watch("finishTime");
  const messageIdWatch = formContext.watch("id");
  const isDirtyAlt = dirtyFields && !!Object.keys(dirtyFields).length;
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
 
  const isServiceOrSpecialOffer = typeWatch === MessageTypes.Service || typeWatch === MessageTypes.SpecialOffer;

  useEffect(() => {
    if (!isDirtyAlt) return;

    const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirtyAlt])

  const handleSubTypeChange = (formFieldOnChange: (value: any) => void, newSubTypeValue: any) => {
    // check custom online tag
    const prevSubTypeValue = allSubtypesOptions.find((option => option.value === subTypeWatch)) ?? { defaultOnlineTag: "" };
    const currentonlineTag = formContext.getValues("onlineTag");
    const hasCustomOnlineTag = currentonlineTag !== prevSubTypeValue.defaultOnlineTag;
    // trigger form field change
    formFieldOnChange(newSubTypeValue);
    // Set online tag values
    const subType = allSubtypesOptions.find((option) => option.value === newSubTypeValue);
    const hasDefaultOnlineTag = !!subType?.defaultOnlineTag;
    let onlineTagValue = '';
    if (hasDefaultOnlineTag) {
      onlineTagValue = subType?.defaultOnlineTag ?? "";
      setIsOnlineTagDisabled(true);
    } else if (hasCustomOnlineTag) {
      onlineTagValue = currentonlineTag;
      setIsOnlineTagDisabled(false);
    }       
    setIsOnlineTagDisabled(false);

    formContext.setValue("hasDefaultOnlineTag", hasDefaultOnlineTag);
    formContext.setValue("onlineTag", onlineTagValue);
    formContext.setValue("hasCustomOnlineTag", hasCustomOnlineTag);
    if (!formContext.getValues("priority")) {
      formContext.setValue("priority", subType?.defaultPriority ?? 1);
    }
  }

 
  const getSubtypeOptions = () => {
    const options = (() => {
      switch (typeWatch) {
        case MessageTypes.Message:
          return messageSubtypesOptions;
        case MessageTypes.SpecialOffer:
          return specialOfferSubtypesOptions;
        case MessageTypes.Service:
          return servicesSubtypesOptions;
        default:
          return [];
      }
    })();
  
    return options.map(option => ({
      ...option,
      label: getSubTypeLabel(option.value as MessageSubtypes)
    }));
  }

  const handleOnlineTagEditClick = () => {
    if (isOnlineTagDisabled) {
      setIsOnlineTagDisabled(false);
      return;
    }

    const subType = allSubtypesOptions.find((option) => option.value === subTypeWatch);
    formContext.setValue("onlineTag", subType?.defaultOnlineTag ?? "");
    setIsOnlineTagDisabled(true);
  };

  const onCancelClick = () => {
      const consts = getConsts();
      history.push(consts.routerPath.messaging);
    
  };

  const onSaveClick = () => {
    formContext.handleSubmit((data) => {
      props.onFormSubmit(data);
      setIsFormSubmitted(true);
    }, props.onFormError)();
  };

  const isPastDate = (date) => {
    return moment(date).isBefore(moment().startOf('day'));
  };

  const isBeforeStartDate = (date, startDate) => {
    return startDate && moment(date).isBefore(moment(startDate, "DD/MM/YYYY"), 'day');
  };

  return (
    <DatePickerProvider>
      <Prompt
        when={isDirtyAlt && !isFormSubmitted}
        message="You have unsaved changes. Are you sure you want to leave?"
      />
      <form>
        <Grid container spacing={3}>
          <Grid xs={8}>
            <Grid container {...gridBorder} {...gridSpacing} sx={{ backgroundColor: "white" }}>
              <Grid width="100%">
                <h2>Template details</h2>
              </Grid>

              <Grid width="50%">
                <label>Template type</label>
                <Controller
                  name="type"
                  control={formContext.control}
                  render={({ field }) => {
                    return (
                      <SelectField
                        name={field.name}
                        label="Type"
                        value={field.value ?? ""}
                        options={messageTypesOptions}
                        onChange={field.onChange}
                        hasError={!!formContext.formState.errors?.type}
                        errorMessage={formContext.formState.errors?.type?.message?.toString()}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid width="50%">
                <label>Subtype</label>
                <Controller
                  name="subType"
                  control={formContext.control}
                  render={({ field }) => (
                    <SelectField
                      name={field.name ?? ""}
                      label="Subtype"
                      value={field.value ?? ""}
                      options={getSubtypeOptions()}
                      onChange={(value) => handleSubTypeChange(field.onChange, value)}
                      hasError={!!formContext.formState.errors?.subType}
                      errorMessage={formContext.formState.errors?.subType?.message?.toString()}
                    />
                  )}
                />
              </Grid>

              {subTypeWatch === MessageSubtypes.Cashback && (
                <>
                  <Grid width="50%">
                    <label>Cashback amount</label>
                    <Controller
                      name="cashbackAmount"
                      control={formContext.control}
                      render={({ field }) => (
                        <TextField
                          id={field.name}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          type={InputType.number}
                          min="0"
                          autoComplete={AutoCompleteType.off}
                          hasError={!!formContext.formState.errors?.cashbackAmount}
                          errorMsg={formContext.formState.errors.cashbackAmount?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid width="50%"></Grid>
                </>
              )}

              <Grid width="100%">
                <label>Template name</label>
                <Controller
                  name="name"
                  control={formContext.control}
                  render={({ field }) => (
                    <TextField
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      autoComplete={AutoCompleteType.off}
                      hasError={!!formContext.formState.errors?.name}
                      errorMsg={formContext.formState.errors.name?.message?.toString()}
                    />
                  )}
                />
                {!formContext.formState.errors.name && <small>This name is for internal use</small>}
              </Grid>

              {isServiceOrSpecialOffer && (
                <Grid width="50%">
                  <Stack direction="column">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <label>Online Tag</label>
                    <Tooltip 
                      title="Add a custom text tag or select from: JB Gift Card; Cashback; Bonus Offer; Competition; Signed Offer. Leave blank for no tag display" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontWeight: 'normal',
                          },
                        },
                      }}
                    ><InfoIconOutlined sx ={{color: '#212121'}} fontSize="small" />
                    </Tooltip>
                  </Stack>
                    <Stack direction="row" spacing={2}>
                      <Controller
                        name="onlineTag"
                        control={formContext.control}
                        render={({ field }) => (
                          <TextField
                            id={field.name}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            autoComplete={AutoCompleteType.off}
                            hasError={!!formContext.formState.errors?.onlineTag}
                            errorMsg={formContext.formState.errors.onlineTag?.message?.toString()}
                            disabled={isOnlineTagDisabled}
                            className={classes.onlineTagInput}
                          />
                        )}
                      />
                      {hasDefaultOnlineTagWatch && (
                        <Button type="secondary" onClick={handleOnlineTagEditClick}>
                          {isOnlineTagDisabled ? "Edit" : "Reset to default"}
                        </Button>
                      )}
                    </Stack>
                    {!hasDefaultOnlineTagWatch && !formContext.formState.errors.onlineTag && (
                      <small>There is no default online tag for this subtype</small>
                    )}
                  </Stack>
                </Grid>
              )}

              <Grid width="100%">
                <label>Heading</label>
                <Controller
                  name="heading"
                  control={formContext.control}
                  render={({ field }) => (
                    <TextField
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      autoComplete={AutoCompleteType.off}
                      hasError={!!formContext.formState.errors?.heading}
                      errorMsg={formContext.formState.errors.heading?.message?.toString()}
                    />
                  )}
                />
                {!formContext.formState.errors.heading && <small>This title will be displayed in the website</small>}
              </Grid>

              {isServiceOrSpecialOffer && (
                <Grid width="100%">
                  <label>Short description</label>
                  <Controller
                    name="shortDescription"
                    control={formContext.control}
                    render={({ field }) => (
                      <TextField
                        id={field.name}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        autoComplete={AutoCompleteType.off}
                        hasError={!!formContext.formState.errors?.shortDescription}
                        errorMsg={formContext.formState.errors.shortDescription?.message?.toString()}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid width="100%">
                <label>Description</label>
                <Controller
                  name="description"
                  control={formContext.control}
                  render={({ field }) => (
                    <MessageRichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      hasError={!!formContext.formState.errors?.description}
                      errorMessage={formContext.formState.errors.description?.message?.toString()}
                      minHeight={200}
                      isTypeMessage={typeWatch === MessageTypes.Message}
                    />
              )}
            />
          </Grid>

              {isServiceOrSpecialOffer && (
                <>
                  <Grid xs={4}>
                    <label>Tile image</label>
                    <Controller
                      name="tileImage"
                      control={formContext.control}
                      render={({ field }) => (
                        <ImageUploadField
                          value={field.value?.file || field.value}
                          onChange={field.onChange}
                          maxWidth={120}
                          maxHeight={120}
                          helperText="This image will be the tile anchor"
                          exactSize
                        />
                      )}
                    />
                  </Grid>

                  <Grid xs={8}>
                    <label>Hero banner image</label>
                    <Controller
                      name="heroBannerImage"
                      control={formContext.control}
                      render={({ field }) => (
                        <ImageUploadField
                          value={field.value?.file || field.value}
                          onChange={field.onChange}
                          maxWidth={1380}
                          maxHeight={400}
                          helperText="This image will be displayed in the more details modal"
                          exactSize
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Grid container {...gridBorder} {...gridSpacing} sx={{ backgroundColor: "white" }}>
              <Grid width="100%">
                <h2>Product information</h2>
              </Grid>

              <Grid width="100%">
                  <label>Search by:</label>
                  <Controller
                    name="conditionTypes"
                    control={formContext.control}
                    render={({ field }) => (
                      <Stack display="inline-flex" marginLeft={3}>
                        <SelectField
                          name={field.name}
                          label="Type"
                          value={field.value?.[0] ?? ""}
                          options={conditionTypeOptions}
                          onChange={(value) => {
                            formContext.setValue("conditionTypes", [value]);
                          }}
                        width="170px"
                          hasError={!!formContext.formState.errors?.conditionTypes}
                          errorMessage={formContext.formState.errors?.conditionTypes?.message?.toString()}
                        />
                      </Stack>
                    )}
                  />
              </Grid>

              {(conditionTypesWatch.includes(ConditionTypes.ProductGroup) || 
                conditionTypesWatch.includes(ConditionTypes.ProductCategory) || 
                conditionTypesWatch.includes(ConditionTypes.Specification)) && (
                <Grid width="100%">
                  <Controller
                    name="excludeMarketplaceProducts"
                    control={formContext.control}
                    render={({ field }) => (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          id="excludeMarketplaceProducts"
                          style={{ accentColor: "green", cursor: 'pointer' }}
                        />
                        <label htmlFor="excludeMarketplaceProducts" style={{ fontSize: 15, cursor: 'pointer' }}>
                          Exclude marketplace products
                        </label>
                      </Stack>
                    )}
                  />
                </Grid>
              )}

              {conditionTypesWatch.includes(ConditionTypes.Specification) && (
                <Grid width="50%">
                  <GroupedSpecificationsDropdown />
                </Grid>
              )}

              {conditionTypesWatch.includes(ConditionTypes.Sku) && (
                <Grid width="100%">
                  <Controller
                    name="skuConditions"
                    control={formContext.control}
                    render={({ field }) => (
                      <ProductSelect
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!formContext.formState.errors?.skuConditions}
                        errorMessage={formContext.formState.errors.skuConditions?.message?.toString()}
                      />
                    )}
                  />
                </Grid>
              )}

              {conditionTypesWatch.includes(ConditionTypes.ProductGroup) && (
                <Grid width="100%">
                  <Controller
                    name="productGroupConditions"
                    control={formContext.control}
                    render={({ field }) => (
                      <ProductGroupSelect 
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!formContext.formState.errors?.productGroupConditions}
                        errorMessage={formContext.formState.errors.productGroupConditions?.message?.toString()}
                      />
                    )}
                  />
                </Grid>
              )}

              {conditionTypesWatch.includes(ConditionTypes.ProductCategory) && (
                <Grid width="100%">
                  <Controller
                    name="productCategoryConditions"
                    control={formContext.control}
                    render={({ field }) => (
                      <ProductCategorySelect
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!formContext.formState.errors?.productCategoryConditions}
                        errorMessage={formContext.formState.errors.productCategoryConditions?.message?.toString()}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid xs={4} sx={{marginTop: "35px", paddingY: 4, paddingX: 2, height: "fit-content", backgroundColor: "white"}} {...gridBorder} spacing={3}>
            <Grid container spacing={2}>
              <Grid width="100%">
                <h2>Scheduling</h2>
              </Grid>
             <Grid width="50%">
                <label>Start date</label>
                <Controller
                  name="startDate"
                  control={formContext.control}
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      value={field.value ? moment(field.value, "DD/MM/YYYY") : null}
                      onChange={(e) => field.onChange(e ? moment(e).format("DD/MM/YYYY") : null)}
                      format="DD/MM/YYYY"
                      shouldDisableDate={isPastDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formContext.formState.errors?.startDate,
                          helperText: formContext.formState.errors?.startDate?.message?.toString(),
                        },
                        day: {
                          sx: {
                            "&.Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.38)",
                                backgroundColor: "transparent",
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid width="50%">
                <label>Start time</label>
                <Controller
                  name="startTime"
                  control={formContext.control}
                  defaultValue={ !!messageIdWatch && !startTimeWatch ? "" : moment().startOf("day").format("HH:mm:ss")} // Default to 12:00 AM
                  render={({ field }) => (
                    <TimeField
                      name={field.name}
                      value={field.value ? moment(field.value, "HH:mm:ss") : null}
                      onChange={(e) => field.onChange(e?.format("HH:mm:ss") ?? "")}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formContext.formState.errors?.startTime,
                          helperText: formContext.formState.errors?.startTime?.message?.toString(),
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid width="50%">
                <label>Finish date</label>
                <Controller
                  name="finishDate"
                  control={formContext.control}
                  render={({ field }) => (
                    <DatePicker
                      name={field.name}
                      value={field.value ? moment(field.value, "DD/MM/YYYY") : null}
                      onChange={(e) => field.onChange(e ? moment(e).format("DD/MM/YYYY") : null)}
                      format="DD/MM/YYYY"
                      shouldDisableDate={(date) => {
                        const startDate = formContext.getValues("startDate");
                        return isPastDate(date) || isBeforeStartDate(date, startDate);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formContext.formState.errors?.finishDate,
                          helperText: formContext.formState.errors?.finishDate?.message?.toString(),
                        },
                        day: {
                          sx: {
                            "&.Mui-disabled": {
                              color: "gray",
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid width="50%">
                <label>Finish time</label>
                <Controller
                  name="finishTime"
                  control={formContext.control}
                  defaultValue={ !!messageIdWatch && !finishTimeWatch ? "" : moment().endOf("day").format("HH:mm:ss")} // Default to 12:00 AM
                  render={({ field }) => (
                    <TimeField
                      name={field.name}
                      value={field.value ? moment(field.value, "HH:mm:ss") : null}
                      onChange={(e) => field.onChange(e?.format("HH:mm:ss") ?? "")}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formContext.formState.errors?.finishTime,
                          helperText: formContext.formState.errors?.finishTime?.message?.toString(),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              
              <Grid sx={{ width: '100%'}}>
                <h2>Visibility</h2>
              </Grid>

              <Grid width="30%">
                <label style={{ fontWeight: 600 }}>Priority</label>
                <Controller
                  name="priority"
                  control={formContext.control}
                  render={({ field }) => (
                    <TextField
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onChange={(e) => {
                        const value = Math.max(1, Math.min(20, Math.floor(Number(e.target.value))));
                        field.onChange(value);
                      }}
                      type={InputType.number}
                      autoComplete={AutoCompleteType.off}
                      hasError={!!formContext.formState.errors?.priority}
                      errorMsg={formContext.formState.errors.priority?.message?.toString()}
                    />
                  )}
                />
              </Grid>

              {process.env.REACT_APP_PRODUCT_MESSAGE_SHOW_ON_FEATURE_ENABLED === "true" && (
                <Grid width="70%">
                  <label style={{ marginBottom: 4, fontWeight: 600 }}>Show on:</label>
                  <Stack>
                    <Controller
                      name="showOn"
                      control={formContext.control}
                      render={({ field }) => (
                        <>
                          <Stack direction="row">
                            <input
                              type="checkbox"
                              checked={field.value.includes("PDP")}
                              onChange={e => {
                                const newValue = e.target.checked
                                ? [...field.value, "PDP"]
                                : field.value.filter(v => v !== "PDP");
                              field.onChange(newValue);
                              }}
                              id="showOnPDP"
                              style={{accentColor: "green"}}
                            />
                            <label htmlFor="showOnPDP" style={{fontSize: 15}}>PDP</label>
                          </Stack>
                          <Stack direction="row" alignItems="start" marginTop="8px">
                            <input
                              type="checkbox"
                              checked={field.value.includes("Email")}
                              onChange={e => {
                                const newValue = e.target.checked
                                  ? [...field.value, "Email"]
                                  : field.value.filter(v => v !== "Email");
                                field.onChange(newValue);
                              }}
                              id="showOnOrderConfirmationEmail"
                              style={{accentColor: "green"}}
                            />
                            <label htmlFor="showOnOrderConfirmationEmail" style={{fontSize: 15}}>Order confirmation email</label>
                          </Stack>
                        </>
                      )}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>
            <Grid marginTop={8}>
              <Grid display="flex" justifyContent="space-between">
                <Button type="link" onClick={onCancelClick}>
                  Cancel
                </Button>
                <Button type="primary" onClick={onSaveClick}>
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <input type="hidden" {...formContext.register("id")} />
        <input type="hidden" {...formContext.register("start")} />
        <input type="hidden" {...formContext.register("finish")} />
      </form>
    </DatePickerProvider>
  );
};
